import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import projects from '../data/projects';
import PhotoGrid from './PhotoGrid';
import PullQuote from './PullQuote';
import Lightbox from './Lightbox';
import './ProjectPage.css';

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} />
  );
}

function AnimatedDivider() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.5 });
  return (
    <motion.div
      ref={ref}
      className="animated-divider"
      initial={{ scaleX: 0 }}
      animate={inView ? { scaleX: 1 } : {}}
      transition={{ duration: 1, ease: [0.77, 0, 0.175, 1] }}
    />
  );
}

function ProjectPage() {
  const { slug } = useParams();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 800], [1.05, 1.15]);
  const heroOverlayOpacity = useTransform(scrollY, [0, 600], [1, 0.3]);

  const projectIndex = projects.findIndex((p) => p.slug === slug);
  const project = projects[projectIndex];

  const prevProject = projectIndex > 0 ? projects[projectIndex - 1] : null;
  const nextProject = projectIndex < projects.length - 1 ? projects[projectIndex + 1] : null;

  const [descRef, descInView] = useInView({ triggerOnce: true, threshold: 0.15 });
  const [navRef, navInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const nextPhoto = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % project.photos.length);
  }, [project]);
  const prevPhoto = useCallback(() => {
    setLightboxIndex((i) => (i - 1 + project.photos.length) % project.photos.length);
  }, [project]);

  if (!project) {
    return (
      <div className="project-not-found">
        <h2>Project Not Found</h2>
        <Link to="/">Return Home</Link>
      </div>
    );
  }

  const firstHalf = project.photos.slice(0, Math.ceil(project.photos.length / 2));
  const secondHalf = project.photos.slice(Math.ceil(project.photos.length / 2));

  return (
    <motion.div
      className="project-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <ScrollProgress />

      {/* Full-bleed Hero */}
      <section className="project-hero">
        <div className="project-hero-image">
          <motion.img
            src={project.heroImage}
            alt={project.title}
            style={{ y: heroY, scale: heroScale }}
          />
        </div>
        <motion.div className="project-hero-overlay" style={{ opacity: heroOverlayOpacity }} />
        <motion.div
          className="project-hero-content"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.77, 0, 0.175, 1] }}
        >
          <motion.span
            className="project-hero-location"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {project.location}
          </motion.span>
          <h1>{project.title}</h1>
          <motion.div
            className="project-hero-line"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.77, 0, 0.175, 1] }}
          />
        </motion.div>
      </section>

      {/* Description Section */}
      <section className="project-description" ref={descRef}>
        <div className="project-description-inner">
          <motion.span
            className="project-location-label"
            initial={{ opacity: 0, y: 20 }}
            animate={descInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          >
            {project.location}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={descInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
          >
            {project.title}
          </motion.h2>
          {project.description.map((paragraph, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 25 }}
              animate={descInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 + i * 0.15, ease: [0.4, 0, 0.2, 1] }}
            >
              {paragraph}
            </motion.p>
          ))}
          <motion.span
            className="project-date"
            initial={{ opacity: 0 }}
            animate={descInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {project.date}
          </motion.span>
        </div>
      </section>

      <AnimatedDivider />

      {/* Brand Break */}
      <PullQuote backgroundImage={project.photos[2]?.src} />

      {/* First Half of Photos */}
      <section className="project-photos-section">
        <PhotoGrid photos={firstHalf} onPhotoClick={openLightbox} />
      </section>

      {/* Brand Break */}
      <PullQuote backgroundImage={project.photos[project.photos.length - 2]?.src} />

      {/* Second Half of Photos */}
      {secondHalf.length > 0 && (
        <section className="project-photos-section">
          <PhotoGrid
            photos={secondHalf}
            onPhotoClick={(index) => openLightbox(index + firstHalf.length)}
          />
        </section>
      )}

      <AnimatedDivider />

      {/* Prev/Next Navigation */}
      <section className="project-nav" ref={navRef}>
        {prevProject ? (
          <motion.div
            className="project-nav-link-wrapper"
            initial={{ opacity: 0, x: -40 }}
            animate={navInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
          >
            <Link to={`/portfolio/${prevProject.slug}`} className="project-nav-link project-nav-prev">
              <span className="project-nav-arrow">&larr;</span>
              <div>
                <span className="project-nav-label">Previous</span>
                <span className="project-nav-title">{prevProject.title}</span>
              </div>
            </Link>
          </motion.div>
        ) : (
          <div />
        )}
        {nextProject ? (
          <motion.div
            className="project-nav-link-wrapper"
            initial={{ opacity: 0, x: 40 }}
            animate={navInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
          >
            <Link to={`/portfolio/${nextProject.slug}`} className="project-nav-link project-nav-next">
              <div>
                <span className="project-nav-label">Next</span>
                <span className="project-nav-title">{nextProject.title}</span>
              </div>
              <span className="project-nav-arrow">&rarr;</span>
            </Link>
          </motion.div>
        ) : (
          <div />
        )}
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          photos={project.photos}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNext={nextPhoto}
          onPrev={prevPhoto}
        />
      )}
    </motion.div>
  );
}

export default ProjectPage;
